// The sheets that are not practice: the menu, Calibrate, Truth mode, Diagnostics, About and the keys.

import { useEffect } from 'preact/hooks';
import { engine } from '../engine/engine.js';
import { HOW_TO_TELL, reviewTaps, startOver, undo } from './actions.js';
import { calibration, calibrationTap, describeOffset, finishCalibration, startCalibration } from './calibrate-run.js';
import { Choice, Switch } from './controls.js';
import { useEngineState } from './engine-state.js';
import { exportLog, exportTruth, pickLog } from './files.js';
import { UpdateNotice } from './home.js';
import { Sheet } from './sheet.js';
import { AboutSponsor, MenuSponsor } from './sponsor-ui.js';
import { closeSheet, openSheet, setFind, setKeep, setOnBeat, ui, type OnBeat } from './ui.js';

// ── menu ──

export function MenuSheet() {
  const s = useEngineState();
  const { on, keep, find } = ui.use();
  const file = s.source === 'file';
  const go = (sheet: Parameters<typeof openSheet>[0]) => () => openSheet(sheet);
  return (
    <Sheet title="Menu">
      <UpdateNotice />
      <div class="field">
        <div class="field-label">I dance</div>
        <Choice<OnBeat>
          label="I dance On1 or On2"
          value={on}
          options={[{ value: 1, label: 'On1', name: 'On1: emphasise 1 and 5' }, { value: 2, label: 'On2', name: 'On2: emphasise 2 and 6' }]}
          onChange={setOnBeat}
        />
        <p class="note">{on === 1 ? 'The phrase bar stands out at 1 and 5.' : 'The phrase bar stands out at 2 and 6.'}</p>
      </div>
      <Switch label="Keep on this device" hint={keep ? 'A copy of each song you open is kept for one-tap reopening.' : 'Off: you choose the file each time.'} checked={keep} onChange={setKeep} />
      <Switch label="Find the count automatically" hint={find ? 'A count is proposed when you open a song. You confirm it or fix it.' : 'Off: tap when you feel 1.'} checked={find} onChange={setFind} />
      <ul class="menu">
        <li><button type="button" class="menu-item" onClick={go('calibrate')}>Calibrate</button></li>
        {file && <li><button type="button" class="menu-item" onClick={go('truth')}>Truth mode</button></li>}
        {file && <li><button type="button" class="menu-item" disabled={!s.canUndo || s.truthMode} onClick={() => { closeSheet(); undo(); }}>Undo last change</button></li>}
        {file && <li><button type="button" class="menu-item" disabled={s.truthMode || s.tapRows.length === 0} onClick={() => { closeSheet(); reviewTaps(); }}>Review taps</button></li>}
        {file && <li><button type="button" class="menu-item" data-testid="start-over" disabled={s.truthMode || s.events.length === 0} onClick={() => { closeSheet(); startOver(); }}>Start over</button></li>}
        {file && <li><button type="button" class="menu-item" onClick={() => { closeSheet(); void exportLog(); }}>Export count</button></li>}
        {file && <li><button type="button" class="menu-item" onClick={() => { pickLog(); closeSheet(); }}>Import count</button></li>}
        <li><button type="button" class="menu-item" onClick={go('diagnostics')}>Diagnostics</button></li>
        <li><button type="button" class="menu-item" onClick={go('help')}>Keys and help</button></li>
        <li><button type="button" class="menu-item" onClick={go('about')}>About</button></li>
        <MenuSponsor />
      </ul>
    </Sheet>
  );
}

// ── calibrate ──

export function CalibrateSheet() {
  const s = useEngineState();
  const c = calibration.use();
  // Closing the sheet cancels a run in progress.
  useEffect(() => () => { void finishCalibration(true); }, []);
  const known = s.calibrationOffsetS !== null;
  const current = known ? `${describeOffset(s.calibrationOffsetS!)} ${s.calibrationSaved ? 'Saved.' : 'Not saved: this tab keeps it until you close it.'}` : 'Not measured yet.';
  return (
    <Sheet title="Calibrate">
      {c.running ? (
        <>
          <p class="note">Tap on each click for 20 seconds. It does not matter if you miss some.</p>
          <button type="button" class="btn primary big-tap" onPointerDown={(e) => { e.preventDefault(); calibrationTap(); }} onClick={(e) => { if (e.detail === 0) calibrationTap(); }}>
            Tap on each click
          </button>
          <p class="readout-small" aria-live="off">{c.taps} taps so far</p>
          <button type="button" class="btn" onClick={() => void finishCalibration(true)}>Cancel</button>
        </>
      ) : (
        <>
          <p class="sentence" role="status" data-testid="calibration-result">{c.result ?? current}</p>
          <p class="note">Every phone and speaker is a little late. Tap to a click for 20 seconds and the app will allow for it.</p>
          <button type="button" class="btn primary" onClick={() => void startCalibration()}>{known ? 'Measure again' : 'Start'}</button>
        </>
      )}
    </Sheet>
  );
}

// ── truth mode ──

export function TruthSheet() {
  const s = useEngineState();
  const file = s.source === 'file';
  return (
    <Sheet title="Truth mode">
      <p class="note">The count is hidden. Tap on every 1 through the whole song. Your taps go to a file that the benchmark reads.</p>
      <Switch label="Truth mode" hint={s.truthMode ? 'On. Tap on every 1.' : 'Off'} checked={s.truthMode} disabled={!file} onChange={(on) => engine.setTruthMode(on)} />
      <p class="sentence">{s.truthTaps} {s.truthTaps === 1 ? 'tap' : 'taps'} saved on this device for this song.</p>
      <button type="button" class="btn primary" disabled={!file || s.truthTaps === 0} onClick={() => void exportTruth()}>Export truth file</button>
    </Sheet>
  );
}

// ── about ──

export function AboutSheet() {
  return (
    <Sheet title="About">
      <p class="sentence">Your music stays on this device. The audio is never uploaded: decoding and counting happen in this browser, and with “Keep on this device” on, a copy of each song you open is stored in this browser only, so one tap reopens it. You can remove any copy from Your songs.</p>
      <p class="sentence">What else the app keeps is your taps and fixes, and how late your taps land on this device. They are stored in this browser only, and you can save them to a file.</p>
      <p class="sentence">Nothing is sent to a server, so the app also works with no connection once it has loaded.</p>
      <p class="sentence">The app includes a small model of where the odd counts (1, 3, 5, 7) fall in salsa, with numbers fitted to the Salsa dataset: Gomez-Marin, D., Ospina-Caicedo, R., Diaz-Cely, J., Paz, J., Jorda, S. and Herrera, P., “Salsa, a Dataset for Beat Estimation in Salsa Music” (Zenodo 13120822), licensed CC BY 4.0. Only those numbers are in the app, no audio and no annotations.</p>
      <p class="sentence">The automatic count is a proposal. Answer Yes if it is right; if not, press 1↔5, ½ beat, Too fast or Too slow, or tap on 1 yourself. Your taps always win.</p>
      <p class="sentence">Each time you confirm or correct a count, you can export it to help improve the app. Nothing is sent anywhere unless you send it.</p>
      <AboutSponsor />
    </Sheet>
  );
}

// ── keys ──

const KEYS: [string, string][] = [
  ['T or 1', 'Tap on 1'],
  ['Space', 'Play or pause'],
  ['← →', 'Back or forward one 8-count'],
  ['[ ]', 'Move the 1 one beat earlier or later'],
  ['H', 'Half a beat'],
  ['F', 'Swap 1 and 5'],
  ['L', 'Loop this 8-count on or off'],
  ['C', 'Cues: next setting'],
  ['B', 'Big mode on or off'],
  ['Z', 'Undo'],
  ['U', 'Undo tap: remove the last tap'],
  ['R', 'Review taps'],
  ['Y', 'Yes: the automatic count is right'],
  ['?', 'This list'],
];

export function HelpSheet() {
  return (
    <Sheet title="Keys and help">
      <dl class="facts keys">
        {KEYS.map(([k, v]) => (
          <div key={k} class="fact">
            <dt><kbd>{k}</kbd></dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
      <p class="note">On a phone, tap the stage on every 1. Every fix can be undone from the message that follows it.</p>
    </Sheet>
  );
}

// ── how do I tell? (the automatic count's question, DESIGN §11.1) ──

export function HowSheet() {
  return (
    <Sheet title="How do I tell?">
      {HOW_TO_TELL.map((sentence) => <p key={sentence} class="sentence">{sentence}</p>)}
    </Sheet>
  );
}
